import Question from "@/socio-questionnaire/models/Question";
import Questionnaire from "@/socio-questionnaire/models/Questionnaire";
import Answer from "@/socio-questionnaire/models/Answer";
import {
  QuestionnaireController,
  AnswerController,
} from "@socotec.io/socio-grpc-api/services/questionnaire/questionnaire_services_pb";

import { defineStore } from "pinia";
import { useRepo } from "pinia-orm";
import { useUserStore } from "./user";
import { useService } from "@/api/connectClient";

const questionnaireClient = useService(QuestionnaireController);
const answerClient = useService(AnswerController);

export const useQuestionnaireStore = defineStore("questionnaireStore", {
  state: () => ({
    referencialNodesUuidsQuestionnaireFilter: [],
    allRelatedNodesUuidsFromQuestionnaires: [], // All nodes related to ANY question (checked or unchecked)
    questionnairesFetched: false,
    hasUnarchivedQuestionnaire: false,
    lastQuestionnaireUpdate: "",
    questionnaireHasBeenArchived: false,
    needRedirection: false,
    questionnairesPrefetched: false,
  }),
  getters: {
    getLastQuestionnaireUpdate() {
      return this.lastQuestionnaireUpdate ? this.lastQuestionnaireUpdate : "";
    },
  },

  actions: {
    async setReferencialNodesUuidsQuestionnaireFilter(answers) {
      const questionRepo = useRepo(Question);
      const questionsWithUuids = questionRepo
        .all()
        .filter((q) => q.objectRelatedUuids && q.objectRelatedUuids.length > 0);

      // Collect all nodes from questions that have objectRelatedUuids
      const allRelatedNodes = [];
      const checkedRelatedNodes = [];

      // Create a map of question UUIDs to their answers
      const answerMap = new Map();
      for (const answer of answers) {
        answerMap.set(answer.question, answer.value);
      }

      // Process all questions with objectRelatedUuids
      for (const question of questionsWithUuids) {
        const isChecked = answerMap.get(question.uuid);

        if (
          question.objectRelatedUuids &&
          question.objectRelatedUuids.length > 0
        ) {
          // Collect all related nodes (for tracking)
          allRelatedNodes.push(...question.objectRelatedUuids);

          // Only include nodes from checked answers
          if (isChecked) {
            checkedRelatedNodes.push(...question.objectRelatedUuids);
          }
        }
      }

      // Add nodes from questionnaires that have answers
      let globalReferencialNodesUuidsQuestionnaireFilter =
        this.getAllQuestionnaires()
          .map((questionnaire) => {
            if (Questionnaire.hasAnswers(questionnaire)) {
              return questionnaire.objectRelatedUuids;
            } else {
              return [];
            }
          })
          .flat();

      const finalArray = checkedRelatedNodes.concat(
        globalReferencialNodesUuidsQuestionnaireFilter,
      );

      // INFO - AH - 19/02/2026 Sentinel value is no longer used for frontend filtering.
      // If there are questions with related nodes but none are checked,
      // we send an empty nodesUuids array. Combined with the frontend filterNodeTreeByAllowedUuids,
      // all related nodes will be hidden while unrelated nodes (e.g. rubrique 20 for mission S)
      // remain visible. This fixes the issue where entire missions disappeared.
      const hasQuestionsWithRelatedNodes = allRelatedNodes.length > 0;
      const hasCheckedRelatedNodes = checkedRelatedNodes.length > 0;
      const hasGlobalNodes =
        globalReferencialNodesUuidsQuestionnaireFilter.length > 0;

      let resultArray = finalArray;
      if (
        hasQuestionsWithRelatedNodes &&
        !hasCheckedRelatedNodes &&
        !hasGlobalNodes
      ) {
        // No checked related questions and no global nodes
        // Send empty array - frontend filtering will hide all related nodes
        resultArray = [];
      }

      // Store all related nodes for frontend filtering (to identify unrelated nodes)
      this.allRelatedNodesUuidsFromQuestionnaires = allRelatedNodes;
      this.setReferencialNodesUuids(resultArray);
    },
    async fetchQuestionnairesAndSetReferencialNodes({
      projectMissionCodes,
      projectDestinationCodes,
      projectTypeCodes,
      projectId,
    }) {
      const questionnaireRepo = useRepo(Questionnaire);
      const answerRepo = useRepo(Answer);

      // Reset referencial nodes array
      this.setReferencialNodesUuids([]);
      const headers = {
        filters: JSON.stringify({
          mission_codes: projectMissionCodes,
          destination_codes: projectDestinationCodes,
          type_codes: projectTypeCodes,
          is_archived: false,
          is_user_admin: useUserStore().isDevOrSuperUser,
        }),
        pagination: JSON.stringify({
          page_size: 100,
        }),
      };
      try {
        const response = await questionnaireClient.listWithOnlyDirectQuestions(
          {},
          { headers },
        );
        questionnaireRepo.save(response.results);
        this.questionnairesFetched = true;
        // INFO - 03/01/2022 - AM - We fetch the non-archived questionnaires here too to check if
        // there is at least one for the missions associated to the rapsotec project
        if (questionnaireRepo.all().length) {
          // INFO - 11/01/2022 - NL - Set the referencialNodesUuidsQuestionnaireFilter array to trigger nodes uuids filter in the CanvasViewer page
          await this.fetchProjectAnswers({ projectId });
          await this.setReferencialNodesUuidsQuestionnaireFilter(
            answerRepo.all(),
          );
        }
      } catch (err) {
        console.error(err);
      }
    },
    async fetchProjectAnswers({ projectId }) {
      const answerRepo = useRepo(Answer);
      const headers = {
        filters: JSON.stringify({
          object_related_id: projectId.toString(),
        }),
        pagination: JSON.stringify({
          page_size: 1000,
        }),
      };
      const response = await answerClient.list({}, { headers });
      await answerRepo.save(response.results);
    },
    async fetchQuestionnaires({ filters }) {
      const questionnaireRepo = useRepo(Questionnaire);

      const response = await questionnaireClient.listWithOnlyDirectQuestions(
        {},
        {
          headers: { filters: JSON.stringify(filters) },
        },
      );
      questionnaireRepo.flush();
      questionnaireRepo.save(response.results);
      this.questionnairesFetched = true;
    },

    async fetchAnswers(filters) {
      const answerRepo = useRepo(Answer);
      const response = await answerClient.list(
        {},
        {
          headers: { filters: JSON.stringify(filters) },
        },
      );
      answerRepo.fresh(response.results);
    },

    async sendAnswers(answers) {
      const formattedAnswers = answers.map((answer) => ({
        ...answer,
        objectRelatedId:
          typeof answer.objectRelatedId === "number"
            ? answer.objectRelatedId.toString()
            : answer.objectRelatedId,
      }));
      await answerClient.createOrUpdateMany({
        answers: formattedAnswers,
      });
    },
    async deleteAnswers({ uuids }) {
      await answerClient.deleteMany({ uuids });
    },
    async fetchSimpleQuestionnaires({ filters, create = true }) {
      const questionnaireRepo = useRepo(Questionnaire);
      if (filters?.ordering) {
        filters.ordering = filters.ordering[0];
        if (filters.ordering.includes("missionCodes")) {
          filters.ordering = filters.ordering.replace(
            "missionCodes",
            "mission_codes",
          );
        }
      }
      const response = await questionnaireClient.list(
        {},
        {
          headers: {
            filters: JSON.stringify(filters),
          },
        },
      );
      if (create) {
        questionnaireRepo.fresh(response.results);
      }
      return { questionnaires: questionnaireRepo.all(), count: response.count };
    },
    async createQuestionnaire(data) {
      const questionnaireRepo = useRepo(Questionnaire);
      const result = await questionnaireClient.create(data);
      const entities = questionnaireRepo.save(result);
      return entities;
    },
    async updateQuestionnaire(data) {
      const questionnaireRepo = useRepo(Questionnaire);

      const result = await questionnaireClient.update(data);
      const entities = questionnaireRepo.save(result);
      return entities;
    },
    async archiveQuestionnaire({ uuid, isArchived }) {
      const questionnaireRepo = useRepo(Questionnaire);

      await questionnaireClient.archiveOrUnarchive({
        uuid: uuid,
        isArchived: !isArchived,
      });
      let updatedQuestionnaire = questionnaireRepo.find(uuid);
      updatedQuestionnaire.isArchived = !isArchived;
      questionnaireRepo.save(updatedQuestionnaire);

      return questionnaireRepo.all();
    },
    async getQuestionnaire({ uuid }) {
      const questionnaireRepo = useRepo(Questionnaire);

      const response = await questionnaireClient.retrieve({ uuid });
      if (!response.extraData?.topics) response.extraData = { topics: [] };
      questionnaireRepo.save(response);
      return questionnaireRepo.withAllRecursive(20).find(uuid);
    },
    setReferencialNodesUuids(referencialNodesUuidsQuestionnaireFilter) {
      this.referencialNodesUuidsQuestionnaireFilter =
        referencialNodesUuidsQuestionnaireFilter;
    },
    resetQuestionnaire() {
      this.referencialNodesUuidsQuestionnaireFilter = [];
      this.allRelatedNodesUuidsFromQuestionnaires = [];
      this.questionnairesFetched = false;
      this.hasUnarchivedQuestionnaire = false;
      this.lastQuestionnaireUpdate = "";
      this.questionnaireHasBeenArchived = false;
      this.needRedirection = false;
      this.questionnairesPrefetched = false;
    },
    sethasUnarchivedQuestionnaire(value) {
      this.hasUnarchivedQuestionnaire = value;
    },
    setLastQuestionnaireUpdate(value) {
      this.lastQuestionnaireUpdate = value;
    },
    setQuestionnaireHasBeenArchived(value) {
      this.questionnaireHasBeenArchived = value;
    },
    setNeedRedirection(value) {
      this.needRedirection = value;
    },
    setQuestionnairesPrefetched(value) {
      this.questionnairesPrefetched = value;
    },
    getAllQuestionnaires() {
      const questionnaireRepo = useRepo(Questionnaire);
      return questionnaireRepo
        .withAllRecursive(20)
        .orderBy("order", "asc")
        .get();
    },
  },
});
